<script setup>
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import Icon from '@/Components/UI/Icon.vue'
import { useCurrency } from '@/composables/useCurrency'
import DayView from './DayView.vue'
import WeekView from './WeekView.vue'
import FleetView from './FleetView.vue'

defineOptions({ layout: AppLayout })

const { t } = useI18n()
const { formatDate } = useCurrency()

const props = defineProps({
  view:          { type: String,  required: true },
  date:          { type: String,  required: true },
  range:         { type: Object,  required: true },
  filters:       { type: Object,  required: true },
  vehicles:      { type: Array,   required: true },
  status_counts: { type: Object,  required: true },
  rentals:       { type: Array,   required: true },
  maintenance:   { type: Array,   required: true },
  day_buckets:   { type: Object,  required: true },
})

const tabOptions = computed(() => [
  { value: 'day',   label: t('calendar.tabs.day') },
  { value: 'week',  label: t('calendar.tabs.week') },
  { value: 'fleet', label: t('calendar.tabs.fleet') },
])

const statusOptions = computed(() => [
  { value: 'all',      label: t('calendar.filters.status.all'),      count: props.status_counts.all },
  { value: 'active',   label: t('calendar.filters.status.active'),   count: props.status_counts.active },
  { value: 'pending',  label: t('calendar.filters.status.pending'),  count: props.status_counts.pending },
  { value: 'returned', label: t('calendar.filters.status.returned'), count: props.status_counts.returned },
])

const windowOptions = computed(() => [
  { value: 7,  label: t('calendar.filters.window.7') },
  { value: 14, label: t('calendar.filters.window.14') },
  { value: 30, label: t('calendar.filters.window.30') },
])

const vehicleSelectOptions = computed(() => [
  { value: '', label: t('calendar.filters.vehicle.all') },
  ...props.vehicles.map(v => ({
    value: v.id,
    label: `${v.label}${v.registration ? ` · ${v.registration}` : ''}`,
  })),
])

const periodLabel = computed(() => {
  const from = new Date(props.range.from)
  const to   = new Date(props.range.to)
  if (props.view === 'day') return formatDate(from, { weekday: 'short', month: 'short', day: 'numeric' })
  return `${formatDate(from, { month: 'short', day: 'numeric' })} – ${formatDate(to, { month: 'short', day: 'numeric', year: 'numeric' })}`
})

const navigate = (overrides) => {
  router.get('/calendar', {
    view:       overrides.view       ?? props.view,
    date:       overrides.date       ?? props.date,
    status:     overrides.status     ?? props.filters.status,
    vehicle_id: overrides.vehicle_id ?? props.filters.vehicle_id ?? '',
    window:     overrides.window     ?? props.filters.window,
  }, { preserveState: true, preserveScroll: true })
}

const todayLocalISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const stepDate = (direction) => {
  const d = new Date(props.date + 'T00:00:00')
  const days = props.view === 'day' ? 1 : props.view === 'week' ? 7 : props.filters.window
  d.setDate(d.getDate() + direction * days)
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  navigate({ date: iso })
}

const goToday = () => navigate({ date: todayLocalISO() })

const setView    = (v)  => navigate({ view: v })
const setStatus  = (s)  => navigate({ status: s })
const setWindow  = (w)  => navigate({ window: w })
const setVehicle = (id) => navigate({ vehicle_id: id })

const activeView = computed(() => ({ day: DayView, week: WeekView, fleet: FleetView })[props.view])
</script>

<template>
  <PageShell :title="t('calendar.title')">
    <template #filters>
      <UiSegmented
        :model-value="view"
        :options="tabOptions"
        @update:model-value="setView"
      />

      <div class="inline-flex items-center gap-1 ml-2">
        <UiButton variant="ghost" size="sm" :aria-label="t('calendar.toolbar.prev')" @click="stepDate(-1)">
          <Icon name="chev-l" :size="16" />
        </UiButton>
        <UiButton variant="ghost" size="sm" @click="goToday">{{ t('calendar.toolbar.today') }}</UiButton>
        <UiButton variant="ghost" size="sm" :aria-label="t('calendar.toolbar.next')" @click="stepDate(1)">
          <Icon name="chev-r" :size="16" />
        </UiButton>
        <span class="ml-2 text-sm text-[color:var(--inkSoft)] tabular-nums">{{ periodLabel }}</span>
      </div>

      <UiSegmented
        :model-value="filters.status"
        :options="statusOptions"
        @update:model-value="setStatus"
      />

      <UiSelect
        :model-value="filters.vehicle_id ?? ''"
        :options="vehicleSelectOptions"
        @update:model-value="setVehicle"
      />

      <UiSegmented
        v-if="view === 'fleet'"
        :model-value="filters.window"
        :options="windowOptions"
        @update:model-value="setWindow"
      />
    </template>

    <component
      :is="activeView"
      :range="range"
      :vehicles="vehicles"
      :rentals="rentals"
      :maintenance="maintenance"
      :day-buckets="day_buckets"
      :date="date"
      :window="filters.window"
    />
  </PageShell>
</template>
